import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Save, RotateCcw, Palette, Timer, Target, Swords, Heart, Trophy, Gavel,
  Users, Banknote, Tv, Sigma, HelpCircle, Building2, Sparkles,
} from "lucide-react";
import { fmtTime } from "@/lib/format";

const ICONS = {
  palette: Palette, timer: Timer, target: Target, swords: Swords, heart: Heart,
  trophy: Trophy, gavel: Gavel, users: Users, banknote: Banknote, tv: Tv, sigma: Sigma,
};

function fmtSecs(v) { return fmtTime ? fmtTime(v) : `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`; }
function fmtCurrency(v, sym) { return `${sym || ""} ${Number(v || 0).toLocaleString()}`; }

// A single field editor driven by CONFIG_SCHEMA metadata.
function SchemaField({ item, value, onChange, currencySymbol }) {
  const { kind, label, options, min, max, step, hint } = item;

  const wrapper = (inner) => (
    <div className="space-y-1">
      <div className="flex justify-between items-baseline gap-2">
        <label className="text-[10px] uppercase tracking-widest text-zinc-500">
          {label}
          {hint && (
            <span className="ml-1 inline-flex items-center gap-0.5 text-zinc-600" title={hint}>
              <HelpCircle className="w-3 h-3" />
            </span>
          )}
        </label>
        {kind === "seconds" && <span className="font-mono text-[#00A86B] text-sm">{fmtSecs(value)}</span>}
        {kind === "currency" && <span className="font-mono text-[#00A86B] text-sm">{fmtCurrency(value, currencySymbol)}</span>}
        {kind === "bool" && <span className="font-mono text-zinc-500 text-xs">{value ? "ON" : "OFF"}</span>}
      </div>
      {inner}
      {hint && <p className="text-[10px] text-zinc-600 leading-tight">{hint}</p>}
    </div>
  );

  if (kind === "bool") {
    return wrapper(<Switch checked={!!value} onCheckedChange={onChange} />);
  }
  if (kind === "enum") {
    return wrapper(
      <Select value={String(value ?? "")} onValueChange={onChange}>
        <SelectTrigger className="bg-[#060d09] border-white/10 text-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#0a1611] border-white/10 text-white max-h-72">
          {(options || []).map((opt) => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (kind === "seconds") {
    return wrapper(
      <Slider
        value={[Number(value ?? min ?? 0)]}
        min={min ?? 0}
        max={max ?? 600}
        step={step ?? 1}
        onValueChange={([v]) => onChange(v)}
      />
    );
  }
  if (kind === "int" || kind === "float") {
    return wrapper(
      <Input
        type="number"
        step={step ?? (kind === "float" ? 0.01 : 1)}
        min={min}
        max={max}
        value={value ?? 0}
        onChange={(e) => onChange(kind === "float" ? Number(e.target.value) : parseInt(e.target.value || "0", 10))}
        className="bg-[#060d09] border-white/10 text-white font-mono"
      />
    );
  }
  if (kind === "currency") {
    return wrapper(
      <Input
        type="number"
        step={100}
        value={value ?? 0}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        className="bg-[#060d09] border-white/10 text-white font-mono"
      />
    );
  }
  if (kind === "formula") {
    return wrapper(
      <Textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="bg-[#060d09] border-white/10 text-white font-mono text-xs h-20"
      />
    );
  }
  // fallback: string
  return wrapper(
    <Input
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      className="bg-[#060d09] border-white/10 text-white"
    />
  );
}

export default function AdminConfig() {
  const { user } = useAuth();
  const { tenant, reload: reloadTenant } = useTenant();
  const [cfg, setCfg] = useState(null);
  const [schema, setSchema] = useState([]);
  const [groups, setGroups] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [cfgResp, schResp] = await Promise.all([
      api.get("/config"),
      api.get("/config/schema"),
    ]);
    setCfg(cfgResp.data);
    setSchema(schResp.data.schema);
    setGroups(schResp.data.groups);
    setDirty(false);
  };
  useEffect(() => { load(); }, []);

  const setK = (k, v) => { setCfg((prev) => ({ ...prev, [k]: v })); setDirty(true); };
  const setNested = (parent, k, v) => {
    setCfg((prev) => ({ ...prev, [parent]: { ...(prev[parent] || {}), [k]: v } }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/config", cfg);
      toast.success("Config saved — engine adopts new values on next state change");
      setDirty(false);
      await reloadTenant();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const reset = async () => {
    if (!window.confirm("Reset ALL config to defaults for this tenant?")) return;
    try {
      await api.post("/config/reset");
      toast.success("Defaults restored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Group schema entries by group key.
  const grouped = useMemo(() => {
    const g = {};
    for (const it of schema) {
      if (!g[it.group]) g[it.group] = [];
      g[it.group].push(it);
    }
    return g;
  }, [schema]);

  if (!user || !["super_admin", "league_admin"].includes(user.role)) {
    return <div className="p-8 text-zinc-400">Super Admin or League Admin only.</div>;
  }
  if (!cfg || schema.length === 0) return <div className="p-8 text-zinc-500">Loading…</div>;

  const orderedGroups = [...groups].sort((a, b) => (a.order || 99) - (b.order || 99));
  const currencySymbol = cfg.currency_symbol || cfg.currency || "";

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            Global Rules Configurator · §13 Master Variable Room
          </div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white">Master Config</h1>
          <div className="text-xs text-zinc-400 mt-1">
            Every timer, fee, rule, and prize amount. Changes propagate instantly to the match engine, auction, broadcast overlays, and public portal.
          </div>
          {tenant && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-white/10 bg-[#0d1c15]">
              <div className="w-5 h-5 rounded flex items-center justify-center text-[9px] font-black text-white"
                   style={{ background: tenant.theme_config?.brand_primary || "#DC2626" }}>
                {tenant.name?.slice(0, 1)}
              </div>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Editing config for tenant</span>
              <span className="text-sm text-white font-medium">{tenant.name}</span>
              <Badge variant="outline" className="border-emerald-800 text-emerald-500 text-[10px]">{tenant.sport_type}</Badge>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button data-testid="btn-config-reset" onClick={reset} variant="outline"
            className="border-red-500/30 text-red-400 hover:bg-red-500/10">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Defaults
          </Button>
          <Button data-testid="btn-config-save" disabled={!dirty || saving} onClick={save}
            className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold disabled:opacity-40">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : dirty ? "Save Changes" : "Saved"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue={orderedGroups[0]?.key || "branding"} className="w-full">
        <TabsList className="bg-[#11241b] border border-white/10 mb-4 flex flex-wrap h-auto">
          {orderedGroups.map((g) => {
            const Icon = ICONS[g.icon] || Sparkles;
            return (
              <TabsTrigger
                key={g.key}
                data-testid={`tab-${g.key}`}
                value={g.key}
                className="data-[state=active]:bg-[#00A86B] data-[state=active]:text-white flex items-center gap-1.5"
              >
                <Icon className="w-3.5 h-3.5" /> {g.label}
              </TabsTrigger>
            );
          })}
          <TabsTrigger value="legacy" className="data-[state=active]:bg-[#00A86B] data-[state=active]:text-white flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5" /> Legacy · Prize
          </TabsTrigger>
        </TabsList>

        {orderedGroups.map((g) => {
          const items = grouped[g.key] || [];
          return (
            <TabsContent key={g.key} value={g.key}>
              <Card className="p-6 bg-[#11241b] border-white/10">
                <div className="grid md:grid-cols-2 gap-6">
                  {items.map((it) => (
                    <SchemaField
                      key={it.key}
                      item={it}
                      value={cfg[it.key]}
                      onChange={(v) => setK(it.key, v)}
                      currencySymbol={currencySymbol}
                    />
                  ))}
                </div>

                {/* Sponsorship: extra list/matrix editors */}
                {g.key === "broadcast" && (
                  <div className="mt-8 border-t border-white/10 pt-6">
                    <h3 className="font-anton text-lg uppercase text-white mb-3">Sponsor tiers matrix</h3>
                    <SponsorTiersEditor cfg={cfg} setNested={setNested} setK={setK} currencySymbol={currencySymbol} />
                  </div>
                )}

                {g.key === "auction" && (
                  <div className="mt-8 border-t border-white/10 pt-6">
                    <h3 className="font-anton text-lg uppercase text-white mb-3">Retention costs by rating tier ({currencySymbol})</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      {["high", "mid", "low"].map((tier) => (
                        <div key={tier}>
                          <label className="text-[10px] uppercase tracking-widest text-zinc-500">{tier}</label>
                          <Input
                            type="number"
                            step={500}
                            value={cfg.retention_costs?.[tier] ?? 0}
                            onChange={(e) => setNested("retention_costs", tier, Number(e.target.value || 0))}
                            className="bg-[#060d09] border-white/10 text-white font-mono mt-1"
                          />
                        </div>
                      ))}
                    </div>
                    <h3 className="font-anton text-lg uppercase text-white mt-6 mb-3">Category minimum bids ({currencySymbol})</h3>
                    <div className="grid md:grid-cols-5 gap-3">
                      {["A", "B", "C", "D", "E"].map((c) => (
                        <div key={c}>
                          <label className="text-[10px] uppercase tracking-widest text-zinc-500">Cat {c}</label>
                          <Input
                            type="number"
                            step={500}
                            value={cfg.category_min_bids?.[c] ?? 0}
                            onChange={(e) => setNested("category_min_bids", c, Number(e.target.value || 0))}
                            className="bg-[#060d09] border-white/10 text-white font-mono mt-1"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            </TabsContent>
          );
        })}

        {/* Legacy tab preserves the prize pool ledger + logistics */}
        <TabsContent value="legacy">
          <Card className="p-6 bg-[#11241b] border-white/10 space-y-6">
            <div>
              <h3 className="font-anton text-2xl uppercase text-white mb-4">Prize Pool</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500">Total prize pool ({currencySymbol})</label>
                  <Input type="number" step={10000} value={cfg.prize_pool_total ?? 0}
                    onChange={(e) => setK("prize_pool_total", Number(e.target.value || 0))}
                    className="bg-[#060d09] border-white/10 text-white font-mono mt-1" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                {Object.entries(cfg.prize_distribution || {}).map(([k, v]) => (
                  <div key={k}>
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500">{k.replace(/_/g, " ")}</label>
                    <Input type="number" step={10000} value={v ?? 0}
                      onChange={(e) => setNested("prize_distribution", k, Number(e.target.value || 0))}
                      className="bg-[#060d09] border-white/10 text-white font-mono mt-1" />
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 rounded-lg bg-[#060d09] border border-white/10">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Allocated vs Pool</div>
                <div className="font-mono text-lg mt-1">
                  <span className="text-[#00A86B]">
                    {currencySymbol} {Object.values(cfg.prize_distribution || {}).reduce((a, b) => a + Number(b || 0), 0).toLocaleString()}
                  </span>
                  <span className="text-zinc-500"> / </span>
                  <span className="text-white">{currencySymbol} {Number(cfg.prize_pool_total || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="border-t border-white/10 pt-6">
              <h3 className="font-anton text-2xl uppercase text-white mb-4">Season logistics</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {["league_start_date", "league_end_date", "auction_date", "registration_fee_deadline", "stall_size"].map((k) => (
                  <div key={k}>
                    <label className="text-[10px] uppercase tracking-widest text-zinc-500">{k.replace(/_/g, " ")}</label>
                    <Input value={cfg[k] ?? ""} onChange={(e) => setK(k, e.target.value)}
                      className="bg-[#060d09] border-white/10 text-white mt-1" />
                  </div>
                ))}
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500">Jersey back reserved (inches)</label>
                  <Input type="number" value={cfg.back_jersey_reserved_inches ?? 0}
                    onChange={(e) => setK("back_jersey_reserved_inches", Number(e.target.value || 0))}
                    className="bg-[#060d09] border-white/10 text-white font-mono mt-1" />
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SponsorTiersEditor({ cfg, setNested, setK, currencySymbol }) {
  const tiers = cfg.sponsor_tiers || [];
  const slots = cfg.sponsor_tier_slots || {};
  const values = cfg.sponsor_tier_min_value || {};

  const addTier = () => {
    const name = window.prompt("New sponsor tier key (lowercase, e.g. 'premium'):");
    if (!name) return;
    const k = name.trim().toLowerCase();
    if (!k || tiers.includes(k)) return;
    setK("sponsor_tiers", [...tiers, k]);
    setNested("sponsor_tier_slots", k, 1);
    setNested("sponsor_tier_min_value", k, 100000);
  };

  const removeTier = (k) => {
    setK("sponsor_tiers", tiers.filter((t) => t !== k));
    const nextSlots = { ...slots }; delete nextSlots[k];
    const nextVals = { ...values }; delete nextVals[k];
    setK("sponsor_tier_slots", nextSlots);
    setK("sponsor_tier_min_value", nextVals);
  };

  return (
    <div>
      <div className="rounded-md border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0a1611] text-zinc-500 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-3 py-2">Tier</th>
              <th className="text-right px-3 py-2">Slots</th>
              <th className="text-right px-3 py-2">Min value ({currencySymbol})</th>
              <th className="px-3 py-2 w-16"></th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((tier) => (
              <tr key={tier} className="border-t border-white/5">
                <td className="px-3 py-2 font-medium text-white capitalize">{tier}</td>
                <td className="px-3 py-2">
                  <Input type="number" min={0} value={slots[tier] ?? 0}
                    onChange={(e) => setNested("sponsor_tier_slots", tier, Number(e.target.value || 0))}
                    className="bg-[#060d09] border-white/10 text-white font-mono text-right w-20 ml-auto" />
                </td>
                <td className="px-3 py-2">
                  <Input type="number" step={5000} value={values[tier] ?? 0}
                    onChange={(e) => setNested("sponsor_tier_min_value", tier, Number(e.target.value || 0))}
                    className="bg-[#060d09] border-white/10 text-white font-mono text-right w-32 ml-auto" />
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="outline"
                    className="border-red-900 text-red-500 hover:bg-red-950 h-7 px-2 text-xs"
                    onClick={() => removeTier(tier)}>Remove</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button size="sm" variant="outline"
        className="mt-3 border-emerald-800 text-emerald-400 hover:bg-emerald-950"
        onClick={addTier}>
        + Add tier
      </Button>
    </div>
  );
}
